import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Clock, AlertTriangle, XCircle, Search, Send, Loader2, Mail, Settings, ChevronLeft, ChevronRight, ToggleLeft, ToggleRight, FileText, Calendar, Download, Award, Eye, FolderInput, ChevronDown, Building2, Wrench } from 'lucide-react';
import { cn } from '../../lib/utils';

export function AttestationsTab({ ctx }) {
  const { attPage, attPeriode, attSearch, attTotal, attTotalPages, attestations, expandedAttIds, gedLoading, handleClasserGED, handleDownloadAttestation, handleDownloadCPCG, handleViewAttestation, isAdmin, isSuperAdmin, toggleAttExpand, setAttPage, setAttSearch, setRectifyModal, setAttPeriode } = ctx;
  return (
        <TabsContent value="attestations" className="mt-4 space-y-4">
          {/* Search + filters */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Ref., email, SIRET..."
                className="pl-10"
                value={attSearch}
                onChange={e => { setAttSearch(e.target.value); setAttPage(1); }}
                data-testid="rcds-att-search"
              />
            </div>
            <Select value={attPeriode} onValueChange={v => { setAttPeriode(v); setAttPage(1); }}>
              <SelectTrigger className="w-40" data-testid="rcds-att-periode">
                <SelectValue placeholder="Periode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes periodes</SelectItem>
                <SelectItem value="30j">30 derniers jours</SelectItem>
                <SelectItem value="90j">90 derniers jours</SelectItem>
                <SelectItem value="6m">6 derniers mois</SelectItem>
                <SelectItem value="1an">12 derniers mois</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Count */}
          <div className="flex items-center gap-3 px-1">
            <span className="text-sm text-gray-500">{attTotal} attestation(s) signee(s)</span>
          </div>

          {attestations.length === 0 ? (
            <div className="bg-white rounded-xl border p-12 text-center" data-testid="rcds-att-empty">
              <Award className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-700 font-medium">Aucune attestation trouvee</p>
              <p className="text-sm text-gray-400">Les attestations signees apparaitront ici</p>
            </div>
          ) : (
            <div className="space-y-1" data-testid="rcds-attestations-list">
              {attestations.map((a, idx) => {
                const attId = a.id || a.numero_police || idx;
                const isExpanded = expandedAttIds.has(attId);
                const montant = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(a.montant_ttc || 0);
                const genDate = a.document_rcds_generated_at ? new Date(a.document_rcds_generated_at).toLocaleDateString('fr-FR') : '-';
                return (
                  <div
                    key={attId}
                    className={cn(
                      "rounded-lg border transition-all duration-150",
                      isExpanded ? "border-emerald-200 shadow-sm bg-white" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                    )}
                    data-testid={`rcds-att-row-${idx}`}
                  >
                    {/* Compact row */}
                    <button
                      onClick={() => toggleAttExpand(attId)}
                      className="flex items-center gap-3 px-4 py-2.5 w-full text-left"
                      data-testid={`rcds-att-toggle-${idx}`}
                    >
                      <Award className={cn("h-4 w-4 shrink-0", isExpanded ? "text-emerald-600" : "text-emerald-500")} />
                      <div className="min-w-0 flex-1">
                        <p className={cn("font-medium text-sm truncate", isExpanded ? "text-emerald-700" : "text-gray-900")}>
                          {a.raison_sociale
                            || a.nom_dirigeant
                            || a.email
                            || (a.numero_police ? `Souscription ${a.numero_police}` : 'Entreprise non renseignée')}
                        </p>
                        <p className="text-[10px] text-gray-400 font-mono truncate">{a.numero_police}</p>
                      </div>
                      <Badge className="bg-emerald-700 text-white border-emerald-900 text-[10px] font-semibold border shrink-0">Validee</Badge>
                      <div className="hidden sm:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                        <Building2 className="h-3 w-3" />
                        <span className="font-mono">{a.siret ? `${a.siret.slice(0, 9)}...` : '—'}</span>
                      </div>
                      <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                        <Calendar className="h-3 w-3" />
                        <span>{genDate}</span>
                      </div>
                      <span className="text-sm font-bold text-emerald-700 tabular-nums shrink-0">{montant}</span>
                      <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-emerald-500")} />
                    </button>

                    {/* Expanded panel */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`rcds-att-details-${idx}`}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Col 1: Entreprise */}
                          <div className="space-y-2">
                            <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Entreprise</h4>
                            <div className="space-y-1.5 text-sm">
                              {a.raison_sociale ? (
                                <p className="font-semibold text-gray-900" data-testid={`rcds-att-raison-${idx}`}>{a.raison_sociale}</p>
                              ) : (
                                <p className="font-medium text-amber-700 italic flex items-center gap-1.5" title="Raison sociale absente — vérifiez la fiche souscripteur">
                                  <Building2 className="h-3.5 w-3.5" />
                                  Raison sociale non renseignée
                                </p>
                              )}
                              {a.nom_dirigeant && (
                                <p className="text-gray-600 text-xs">
                                  <span className="text-gray-400">Dirigeant :</span> {a.nom_dirigeant}
                                </p>
                              )}
                              <div className="text-gray-600 flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5 text-gray-400" />
                                SIRET: <span className="font-mono text-xs">{a.siret || '—'}</span>
                              </div>
                              {a.email && <p className="text-gray-600 truncate text-xs">{a.email}</p>}
                            </div>
                          </div>

                          {/* Col 2: Document */}
                          <div className="space-y-2">
                            <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Document</h4>
                            <div className="space-y-1.5 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">N police:</span>
                                <span className="font-mono text-xs font-medium text-gray-800 truncate max-w-[180px]" title={a.numero_police}>{a.numero_police}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Statut:</span>
                                <Badge className="bg-emerald-700 text-white text-[10px] font-semibold">Validee</Badge>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Montant Global:</span>
                                <span className="font-bold text-emerald-700">{montant}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Date generation:</span>
                                <span className="text-gray-700">{genDate}</span>
                              </div>
                            </div>
                          </div>

                          {/* Col 3: Actions */}
                          <div className="space-y-2">
                            <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</h4>
                            <div className="flex flex-wrap gap-1.5">
                              {a.attestation_url && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); handleViewAttestation(a); }}
                                    className="h-8 text-xs gap-1.5"
                                    data-testid={`rcds-att-view-${idx}`}
                                  >
                                    <Eye className="h-3.5 w-3.5" /> Visualiser
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); handleDownloadAttestation(a); }}
                                    className="h-8 text-xs gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                    data-testid={`rcds-att-download-${idx}`}
                                  >
                                    <Download className="h-3.5 w-3.5" /> Attestation signee
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); handleDownloadCPCG(a); }}
                                    className="h-8 text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
                                    data-testid={`rcds-cpcg-download-${idx}`}
                                  >
                                    <FileText className="h-3.5 w-3.5" /> CP/CG signes
                                  </Button>
                                  {isAdmin && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); handleClasserGED(a.id || a.numero_police); }}
                                      disabled={gedLoading === (a.id || a.numero_police)}
                                      className="h-8 text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
                                      data-testid={`rcds-att-ged-${idx}`}
                                    >
                                      {gedLoading === (a.id || a.numero_police) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderInput className="h-3.5 w-3.5" />}
                                      Classer GED
                                    </Button>
                                  )}
                                  {isSuperAdmin && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => { e.stopPropagation(); setRectifyModal({ open: true, souscription_id: a.id || a.numero_police }); }}
                                      className="h-8 text-xs gap-1.5 border-red-300 text-red-700 hover:bg-red-50"
                                      title="Rectifier l'attestation signee (Attestation / CP / CG)"
                                      data-testid={`rcds-att-rectify-${idx}`}
                                    >
                                      <Wrench className="h-3.5 w-3.5" /> Rectifier
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {attTotalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">Page {attPage}/{attTotalPages} ({attTotal} resultats)</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={attPage <= 1} onClick={() => setAttPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" disabled={attPage >= attTotalPages} onClick={() => setAttPage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
  );
}

export function RappelsTab({ ctx }) {
  const { handleToggle, handleTriggerAll, handleUpdateIntervals, reminderConfig, stats, triggeringAll, updatingConfig } = ctx;
  return (
          <TabsContent value="rappels" className="mt-4 space-y-6">
            <Card className="border-2 border-blue-200" data-testid="rcds-reminder-config">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Configuration des rappels RCDS
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleToggle} disabled={updatingConfig} className="text-white hover:bg-white/20" data-testid="rcds-toggle-btn">
                    {updatingConfig ? <Loader2 className="h-4 w-4 animate-spin" /> : reminderConfig?.enabled ? <ToggleRight className="h-6 w-6 text-green-300" /> : <ToggleLeft className="h-6 w-6 text-red-300" />}
                    <span className="ml-2 text-sm">{reminderConfig?.enabled ? 'Active' : 'Desactive'}</span>
                  </Button>
                </CardTitle>
                <CardDescription className="text-blue-100">
                  Rappels automatiques de renouvellement pour les adhesions RCDS expirant bientot
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-5">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3 text-sm">Intervalles (jours avant expiration)</h4>
                    <div className="flex flex-wrap gap-2">
                      {[30, 15, 7, 3, 1].map(day => {
                        const active = (reminderConfig?.intervals_days || [30,15,7,1]).includes(day);
                        return (
                          <Button key={day} size="sm" variant={active ? 'default' : 'outline'}
                            className={active ? 'bg-blue-600 hover:bg-blue-700' : ''}
                            onClick={() => {
                              const cur = reminderConfig?.intervals_days || [30,15,7,1];
                              handleUpdateIntervals(active ? cur.filter(d=>d!==day) : [...cur, day].sort((a,b)=>b-a));
                            }}
                            disabled={updatingConfig}
                          >J-{day}</Button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-3 text-sm">Dernier envoi</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{reminderConfig?.last_run ? new Date(reminderConfig.last_run).toLocaleString('fr-FR') : 'Jamais execute'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{reminderConfig?.emails_sent_today || 0} email(s)</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg flex flex-col justify-center gap-3">
                    <Button onClick={handleTriggerAll} disabled={triggeringAll || !reminderConfig?.enabled} className="bg-blue-600 hover:bg-blue-700 w-full" data-testid="rcds-trigger-btn">
                      {triggeringAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Envoyer tous les rappels
                    </Button>
                    <p className="text-xs text-gray-500 text-center">Envoie un rappel a toutes les adhesions proches de l'expiration</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Résumé expirations dans l'onglet rappels */}
            {stats && (
              <div className="grid md:grid-cols-3 gap-3">
                <Card className="border-orange-200">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 bg-orange-600 rounded-lg"><AlertTriangle className="h-5 w-5 text-white" /></div>
                    <div>
                      <p className="text-2xl font-bold text-orange-700">{stats.expirant_30j}</p>
                      <p className="text-xs text-gray-500">Expirant sous 30j</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-amber-200">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 bg-amber-600 rounded-lg"><Clock className="h-5 w-5 text-white" /></div>
                    <div>
                      <p className="text-2xl font-bold text-amber-700">{stats.expirant_15j}</p>
                      <p className="text-xs text-gray-500">Expirant sous 15j</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-red-200">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="p-2 bg-red-600 rounded-lg"><XCircle className="h-5 w-5 text-white" /></div>
                    <div>
                      <p className="text-2xl font-bold text-red-700">{stats.expirant_7j}</p>
                      <p className="text-xs text-gray-500">Expirant sous 7j</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
  );
}
